import { IText, Point, Control, util, Object as FabricObject } from 'fabric';

// Type definitions
type FabricObjectWithBezier = IText & {
    isBezierWarped?: boolean;
    _render_original_bezier?: (ctx: CanvasRenderingContext2D) => void;
    bezierControls?: { p0: Point, p1: Point, p2: Point, p3: Point };
    baselineOffset?: number;
};

type BezierControl = Control & {
    corner: 'p0' | 'p1' | 'p2' | 'p3';
};


// Bezier mathematics
function cubicPoint(t: number, p0: Point, p1: Point, p2: Point, p3: Point): Point {
    const u = 1 - t;
    const tt = t * t, uu = u * u;
    const ttt = tt * t, uuu = uu * u;
    const x = uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x;
    const y = uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y;
    return new Point(x, y);
}

function cubicDerivative(t: number, p0: Point, p1: Point, p2: Point, p3: Point): Point {
    const u = 1 - t;
    const x = 3 * u * u * (p1.x - p0.x) + 6 * u * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x);
    const y = 3 * u * u * (p1.y - p0.y) + 6 * u * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y);
    return new Point(x, y);
}

// Fabric.js control handlers
function bezierControlPositionHandler(this: BezierControl, dim: any, finalMatrix: any, fabricObject: any) {
    const obj = fabricObject as FabricObjectWithBezier;
    const corner = this.corner;
    // Control points are in object's local coordinates
    const point = obj.bezierControls![corner];
    return util.transformPoint(point, obj.calcTransformMatrix());
}

function bezierControlActionHandler(eventData: MouseEvent, transform: any, x: number, y: number): boolean {
    const target = transform.target as FabricObjectWithBezier;
    const corner = transform.corner as 'p0' | 'p1' | 'p2' | 'p3';
    
    // Transform mouse coordinates from canvas space to object's local space
    const point = util.transformPoint(
      new Point(x, y),
      util.invertTransform(target.calcTransformMatrix())
    );
    
    target.bezierControls![corner].x = point.x;
    target.bezierControls![corner].y = point.y;
    
    target.dirty = true; // Mark object as dirty to trigger re-render
    target.canvas?.requestRenderAll();
    return true;
}

function renderBezierControlIcon(ctx: CanvasRenderingContext2D, left: number, top: number, styleOverride: any, fabricObject: any) {
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(util.degreesToRadians(fabricObject.angle || 0));
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, 2 * Math.PI);
    ctx.fillStyle = '#39d';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
}

// Core rendering function
function customBezierRender(this: FabricObjectWithBezier, ctx: CanvasRenderingContext2D) {
    if (!this.isBezierWarped || !this.bezierControls) {
        if (this._render_original_bezier) {
            this._render_original_bezier(ctx);
        }
        return;
    }

    // 1. Draw control polygon and curve for visualization (only if active)
    if (this.canvas && this.canvas.getActiveObject() === this) {
        const { p0, p1, p2, p3 } = this.bezierControls;
        ctx.save();
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(120,140,255,0.35)";
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.lineTo(p3.x, p3.y);
        ctx.stroke();
        
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#66a3ff";
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.bezierCurveTo(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
        ctx.stroke();
        ctx.restore();
    }

    // 2. Draw the warped text
    drawWarpedText.call(this, ctx);
}

// Text drawing logic, adapted from the HTML file
function drawWarpedText(this: FabricObjectWithBezier, ctx: CanvasRenderingContext2D) {
    const text = this.text || '';
    if (!text || !this.bezierControls) return;

    const { p0, p1, p2, p3 } = this.bezierControls;

    // --- Arc-length parameterization ---
    const sampleN = 400;
    const samples: {t: number, len: number}[] = new Array(sampleN + 1);
    let acc = 0;
    let prev = cubicPoint(0, p0, p1, p2, p3);
    samples[0] = { t: 0, len: 0 };
    for (let i = 1; i <= sampleN; i++) {
        const t = i / sampleN;
        const p = cubicPoint(t, p0, p1, p2, p3);
        acc += Math.hypot(p.x - prev.x, p.y - prev.y);
        samples[i] = { t, len: acc };
        prev = p;
    }
    const totalLen = samples[sampleN].len || 1;

    function tAtLength(targetLen: number): number {
        targetLen = Math.max(0, Math.min(targetLen, totalLen));
        let lo = 0, hi = sampleN;
        while (lo < hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (samples[mid].len < targetLen) lo = mid + 1; else hi = mid;
        }
        const s = samples[Math.max(0, lo - 1)];
        const e = samples[Math.min(sampleN, lo)];
        const segLen = e.len - s.len;
        const ratio = segLen ? (targetLen - s.len) / segLen : 0;
        return s.t + (e.t - s.t) * ratio;
    }
    // --- End of arc-length ---

    // --- Text rendering ---
    ctx.font = this._getFontDeclaration();
    ctx.fillStyle = this.fill as string || '#000';
    
    // Setup stroke if present
    const hasStroke = this.stroke && this.strokeWidth && this.strokeWidth > 0;
    if (hasStroke) {
        ctx.strokeStyle = this.stroke as string;
        ctx.lineWidth = this.strokeWidth || 1;
        ctx.lineJoin = 'round'; // usually looks better for text
        ctx.miterLimit = 2;
    }
    
    ctx.textBaseline = "middle";

    const letterSpacing = this.charSpacing || 0;
    const baselineOffset = this.baselineOffset || 0;
    const lines = text.split(this._reNewline);
    
    // Center the block of lines vertically around the curve
    const lineHeights = this._textLines.map((_line, i) => this.lineHeight);
    const totalHeight = lineHeights.reduce((a: number, b: number) => a + b, 0);
    let currentY = -totalHeight / 2;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineHeight = lineHeights[i];

        if (!line) {
            currentY += lineHeight;
            continue;
        }
        
        const metrics = [];
        let totalWidth = 0;
        for (const char of line) {
            // Using measureText for width and adding letterSpacing manually for arc-length mapping
            const w = ctx.measureText(char).width + letterSpacing;
            metrics.push({ char, w });
            totalWidth += w;
        }
        if (totalWidth <= 0) {
             currentY += lineHeight;
             continue;
        }

        let accumW = 0;
        for (const metric of metrics) {
            const { char, w } = metric;
            const midCharPos = accumW + w / 2;
            const rel = midCharPos / totalWidth;
            const targetLen = rel * totalLen;
            const t = tAtLength(targetLen);

            const p = cubicPoint(t, p0, p1, p2, p3);
            const d = cubicDerivative(t, p0, p1, p2, p3);
            const angle = Math.atan2(d.y, d.x);

            const nx = -d.y;
            const ny = d.x;
            const nlen = Math.hypot(nx, ny) || 1;
            const normalUnit = { x: nx / nlen, y: ny / nlen };
            
            // Apply baseline offset and multi-line stacking
            const offsetAmount = baselineOffset + currentY + lineHeight / 2;

            const finalX = p.x + normalUnit.x * offsetAmount;
            const finalY = p.y + normalUnit.y * offsetAmount;
            
            ctx.save();
            ctx.translate(finalX, finalY);
            ctx.rotate(angle);
            const charWidth = ctx.measureText(char).width;
            ctx.translate(-charWidth / 2, 0);
            
            if (hasStroke) {
                ctx.strokeText(char, 0, 0);
            }
            ctx.fillText(char, 0, 0);
            ctx.restore();

            accumW += w;
        }
        currentY += lineHeight;
    }
}

// Public API
export function enableBezierWarp(textObject: IText, bezierControls?: {p0: Point, p1: Point, p2: Point, p3: Point}, baselineOffset: number = 0) {
    const obj = textObject as FabricObjectWithBezier;
    if (obj.isBezierWarped) return;

    obj._render_original_bezier = obj._render.bind(obj);
    obj._render = customBezierRender;
    
    // Initial control points relative to object's center (0,0)
    const w = obj.width / 2 || 100;
    const h = obj.height / 2 || 20;
    if (bezierControls) {
        obj.bezierControls = bezierControls;
    } else {
        obj.bezierControls = {
            p0: new Point(-w, 0),
            p1: new Point(-w / 2, -h),
            p2: new Point(w / 2, -h),
            p3: new Point(w, 0),
        };
    }
    obj.baselineOffset = baselineOffset;
    
    const controls = obj.controls;
    controls.p0 = new Control({ x: obj.bezierControls.p0.x, y: obj.bezierControls.p0.y, corner: 'p0', render: renderBezierControlIcon, positionHandler: bezierControlPositionHandler, actionHandler: bezierControlActionHandler } as any);
    controls.p1 = new Control({ x: obj.bezierControls.p1.x, y: obj.bezierControls.p1.y, corner: 'p1', render: renderBezierControlIcon, positionHandler: bezierControlPositionHandler, actionHandler: bezierControlActionHandler } as any);
    controls.p2 = new Control({ x: obj.bezierControls.p2.x, y: obj.bezierControls.p2.y, corner: 'p2', render: renderBezierControlIcon, positionHandler: bezierControlPositionHandler, actionHandler: bezierControlActionHandler } as any);
    controls.p3 = new Control({ x: obj.bezierControls.p3.x, y: obj.bezierControls.p3.y, corner: 'p3', render: renderBezierControlIcon, positionHandler: bezierControlPositionHandler, actionHandler: bezierControlActionHandler } as any);

    obj.isBezierWarped = true;
    obj.path = undefined; // Ensure Fabric's default path logic is disabled
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}

export function disableBezierWarp(textObject: IText) {
    const obj = textObject as FabricObjectWithBezier;
    if (!obj.isBezierWarped) return;

    if (obj._render_original_bezier) {
        obj._render = obj._render_original_bezier;
        delete obj._render_original_bezier;
    }
    
    obj.isBezierWarped = false;
    delete obj.controls.p0;
    delete obj.controls.p1;
    delete obj.controls.p2;
    delete obj.controls.p3;
    delete obj.bezierControls;
    delete obj.baselineOffset;
    
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}
